import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import qophService from '../services/qophService';
import networkService from '../services/networkService';
import './QophPage.css';
import ProfileBackground from '../components/ProfileBackground';
import { useAppBackground } from '../context/AppBackgroundContext';
import { useNotification } from '../context/NotificationContext';
import { useSpeech } from '../context/SpeechContext';
import { useAudio } from '../context/AudioContext';
import { VOICE_INTENTS, sanitizeVoicePayload } from '../services/voiceCommandService';
import { useAgentCommands } from '../contexts/AgentCommandContext';
import { useI18n } from '../i18n';
import ChatLayout from '../components/Qoph/ChatLayout';
import { normalizeChats } from '../utils/chatAdapters';
import { useProgression } from '../context/ProgressionContext';
import GuardianVignette from '../components/guardians/GuardianVignette';
import api from '../api';

const QophPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { progressionState, refreshState } = useProgression() || {};
  const { displayedImage, settings } = useAppBackground() || {};
  const { lastCommand } = useSpeech() || {};
  const audio = useAudio();
  const { t } = useI18n();
  const { registerCommand, unregisterCommand } = useAgentCommands();

  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  const [showTutorialAdvance, setShowTutorialAdvance] = useState(false);

  // Group creation modal states
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [newGroupEndDate, setNewGroupEndDate] = useState('');
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [connections, setConnections] = useState([]);

  const inputRef = useRef(null);
  const processedVoiceCommandRef = useRef(null);
  const isAgenticAutoReadPending = useRef(false);

  const fetchAllChats = useCallback(async () => {
    try {
      const [qophRes, groupsRes] = await Promise.all([
        qophService.listChats(),
        networkService.getGroups()
      ]);

      const qChats = Array.isArray(qophRes.data) ? qophRes.data : (qophRes.data?.chats || []);
      const gChats = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data?.groups || []);

      const unified = normalizeChats(qChats, gChats);
      setChats(unified);
    } catch (err) {
      console.error('Error fetching chats:', err);
    }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      // Assuming a valid PIN session or public fetch if possible,
      // but usually NetworkPage handles the PIN.
      // For Group Creation, we need connections.
      // We'll try to fetch without PIN first, or handle 401/403.
      const data = await networkService.getConnections();
      setConnections(data.connections || data);
    } catch (err) {
      console.warn('Could not fetch connections for group creation:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllChats();
  }, [fetchAllChats]);

  const handleSelectChat = async (chat) => {
    setActiveChat(chat);
    setChatLoading(true);
    setMessages([]);
    try {
      let res;
      if (chat.type === 'qoph') {
        res = await qophService.getChatMessages(chat.id);
      } else {
        res = await networkService.getGroupChat(chat.id);
      }
      const msgs = Array.isArray(res.data) ? res.data : (res.data?.messages || res.data?.data || []);
      setMessages(msgs);
    } catch (err) {
      console.error('Error fetching messages:', err);
      addNotification(t('common.error'), 'error');
    } finally {
      setChatLoading(false);
    }
  };

  const handleStartNewChat = useCallback(async (e, dictatedMessage) => {
    e?.preventDefault?.();
    const candidateMessage = dictatedMessage ?? newMessage;
    if (!candidateMessage.trim() || loading) return;

    setLoading(true);
    const msg = sanitizeVoicePayload(candidateMessage);
    setNewMessage('');

    try {
      const { data } = await qophService.createChat(msg);
      const newActive = {
        id: data.chat.id,
        title: data.chat.title,
        type: 'qoph',
        manifesto: data.chat.manifesto,
        updatedAt: data.chat.updatedAt
      };
      setActiveChat(newActive);
      setMessages([{ role: 'user', content: msg, createdAt: new Date().toISOString() }, data.message]);
      fetchAllChats();

      if (progressionState?.stage === 'ORACULO') {
        setShowTutorialAdvance(true);
      }
    } catch (err) {
      addNotification(t('qoph.connectError'), 'error');
      setNewMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [addNotification, fetchAllChats, loading, newMessage, progressionState?.stage, t]);

  const handleSendMessage = useCallback(async (e, dictatedMessage) => {
    e?.preventDefault?.();

    if (activeChat?.id === 'draft') {
      return handleStartNewChat(e, dictatedMessage);
    }

    const candidateMessage = dictatedMessage ?? newMessage;
    if (!candidateMessage.trim() || chatLoading || !activeChat) return;

    const msg = sanitizeVoicePayload(candidateMessage);
    setNewMessage('');

    // Optimistic update
    const tempMsg = { id: Date.now(), role: 'user', content: msg, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);

    setChatLoading(true);
    try {
      let res;
      if (activeChat.type === 'qoph') {
        res = await qophService.sendMessage(activeChat.id, msg);
      } else {
        res = await networkService.sendGroupMessage(activeChat.id, msg);
      }
      setMessages(prev => [...prev.filter(m => m.id !== tempMsg.id), tempMsg, res.data]);
      fetchAllChats();
    } catch (err) {
      addNotification(t('qoph.sendError'), 'error');
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
    } finally {
      setChatLoading(false);
    }
  }, [activeChat, addNotification, chatLoading, fetchAllChats, handleStartNewChat, newMessage, t]);

  const handleNewChat = useCallback(() => {
    setActiveChat({ id: 'draft', type: 'qoph', title: t('qoph.newChat') || 'Nueva conexión' });
    setMessages([]);
    setNewMessage('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [t]);

  useEffect(() => {
    if (chats.length === 0 && progressionState?.stage === 'ORACULO') {
      handleNewChat();
    }
  }, [chats.length, progressionState?.stage, handleNewChat]);

  const handleRenameChat = useCallback(async (id, title) => {
    try {
      await qophService.updateChat(id, title);
      setChats(prev => prev.map(c => c.id === id && c.type === 'qoph' ? { ...c, title } : c));
      if (activeChat?.id === id && activeChat?.type === 'qoph') {
        setActiveChat(prev => ({ ...prev, title }));
      }
      addNotification(t('common.updated'), 'success');
    } catch (err) {
      addNotification(t('qoph.updateTitleError'), 'error');
    }
  }, [activeChat, addNotification, t]);

  const handleDeleteChat = useCallback(async (id) => {
    try {
      await qophService.deleteChat(id);
      setChats(prev => prev.filter(c => !(c.id === id && c.type === 'qoph')));
      if (activeChat?.id === id && activeChat?.type === 'qoph') {
        setActiveChat(null);
        setMessages([]);
      }
      addNotification(t('common.delete'), 'success');
    } catch (err) {
      addNotification(t('qoph.deleteError'), 'error');
    }
  }, [activeChat, addNotification, t]);

  const handleCreateGroup = async () => {
    if (!newGroupTitle || !newGroupEndDate) {
      addNotification(t('network.titleDateRequired'), 'info');
      return;
    }
    setLoading(true);
    try {
      await networkService.createGroup({
        title: newGroupTitle,
        endDate: newGroupEndDate,
        invitedHandles: invitedUsers.map(u => u.handle)
      });
      setShowCreateGroup(false);
      setNewGroupTitle('');
      setNewGroupEndDate('');
      setInvitedUsers([]);
      fetchAllChats();
      addNotification(t('common.create'), 'success');
    } catch (err) {
      addNotification(err.response?.data?.error || t('network.createGroupError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleInvitedUser = (u) => {
    const isMutual = connections.find(c => c.handle === u.handle)?.is_mutual;
    if (!isMutual) {
      addNotification(t('network.mutualRequired', { handle: u.handle }), 'info');
      return;
    }

    if (invitedUsers.find(iu => iu.handle === u.handle)) {
      setInvitedUsers(prev => prev.filter(iu => iu.handle !== u.handle));
    } else {
      if (invitedUsers.length >= 15) {
        addNotification(t('network.inviteLimit'), 'info');
        return;
      }
      setInvitedUsers(prev => [...prev, u]);
    }
  };

  useEffect(() => {
    if (!showCreateGroup || !inviteSearchQuery) {
      setInviteSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await networkService.search(inviteSearchQuery);
        setInviteSearchResults(data);
      } catch (err) {
        console.error(err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inviteSearchQuery, showCreateGroup]);

  const resizeComposer = useCallback((value) => {
    setNewMessage(value);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
      }
    });
  }, []);

  const cleanQophText = useCallback((value = '') => String(value)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(?:\?\S*)?/gi, '')
    .replace(/\s+/g, ' ')
    .trim(), []);

  // Voice command integration (Adapted from original QophPage)
  useEffect(() => {
    const analysis = lastCommand?.analysis;
    if (!analysis?.handled || processedVoiceCommandRef.current === lastCommand.timestamp) return;

    const isQophRead = analysis.intent === VOICE_INTENTS.READ_OUT_LOUD && analysis.target === 'last_qoph_message';
    const isQophSend = analysis.intent === VOICE_INTENTS.SEND_VOICE_MESSAGE && analysis.targetType === 'qoph';
    const isCloseChat = analysis.intent === VOICE_INTENTS.UI_ACTION && analysis.action === 'CLOSE_CHAT';

    if (!isQophRead && !isQophSend && !isCloseChat) return;
    processedVoiceCommandRef.current = lastCommand.timestamp;

    if (isCloseChat) {
      setActiveChat(null);
      setMessages([]);
      return;
    }

    if (isQophRead) {
      const lastQophMessage = [...messages].reverse().find(message => message.role === 'qoph' && message.content);
      const textToRead = cleanQophText(lastQophMessage?.content);
      if (!textToRead) {
        addNotification(t('qoph.noMessageToRead'), 'info');
        return;
      }
      audio?.speak(textToRead, { label: t('qoph.reading') });
      return;
    }

    if (isQophSend) {
      const message = sanitizeVoicePayload(analysis.message);
      if (!message) return;
      resizeComposer(message);
      if (activeChat) {
        handleSendMessage({ preventDefault: () => {} }, message);
      } else {
        handleStartNewChat({ preventDefault: () => {} }, message);
      }
    }
  }, [addNotification, audio, cleanQophText, handleSendMessage, handleStartNewChat, lastCommand, messages, resizeComposer, activeChat, t]);

  useEffect(() => {
    registerCommand('qoph_send_message', (text) => {
      const safeText = sanitizeVoicePayload(text);
      if (!safeText) return false;
      resizeComposer(safeText);
      if (activeChat) {
        handleSendMessage({ preventDefault: () => {} }, safeText);
      } else {
        handleStartNewChat({ preventDefault: () => {} }, safeText);
      }
      return true;
    });

    registerCommand('qoph_read_last_message', () => {
      const lastQophMessage = [...messages].reverse().find(message => message.role === 'qoph' && message.content);
      const textToRead = cleanQophText(lastQophMessage?.content);
      if (!textToRead) return false;
      audio?.speak(textToRead, { label: t('qoph.reading') });
      return true;
    });

    return () => {
      unregisterCommand('qoph_send_message');
      unregisterCommand('qoph_read_last_message');
    };
  }, [audio, cleanQophText, handleSendMessage, handleStartNewChat, messages, registerCommand, resizeComposer, activeChat, t, unregisterCommand]);

  useEffect(() => {
    if (location.state?.agenticPrompt) {
      const prompt = location.state.agenticPrompt;
      navigate(location.pathname, { replace: true });
      isAgenticAutoReadPending.current = true;

      const safePrompt = sanitizeVoicePayload(prompt);
      if (safePrompt) {
        resizeComposer(safePrompt);
        if (activeChat) {
          handleSendMessage({ preventDefault: () => {} }, safePrompt);
        } else {
          handleStartNewChat({ preventDefault: () => {} }, safePrompt);
        }
      }
    }
  }, [location.state, navigate, location.pathname, activeChat, handleSendMessage, handleStartNewChat, resizeComposer]);

  useEffect(() => {
    if (isAgenticAutoReadPending.current && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.role === 'qoph') {
        isAgenticAutoReadPending.current = false;
        const textToRead = cleanQophText(lastMsg.content);
        if (textToRead) {
          audio?.speak(textToRead, { label: t('qoph.reading') });
        }
      }
    }
  }, [messages, cleanQophText, audio, t]);

  return (
    <>
      {showTutorialAdvance && (
        <GuardianVignette
          buttonText={t('guardian.stages.continueBtn')}
          message={t('guardian.stages.qophAdvanceMsg')}
          onContinue={() => {
            api.post('/progression/advance', { nextStage: 'CAJA_MUTANTE' }).then(() => refreshState?.());
            setShowTutorialAdvance(false);
            navigate('/conciencia');
          }}
        />
      )}
      {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}
      <div className="qoph-page-hub" style={{ position: 'relative', zIndex: 2 }}>
        <div className="qoph-header-cyber">
          <h1 className="qoph-title-glitch" data-text="QOPH">QOPH</h1>
          <p className="qoph-subtitle-cyber">{t('qoph.subtitle')}</p>
        </div>

        <ChatLayout
          chats={chats}
          activeChat={activeChat}
          messages={messages}
          onSelectChat={handleSelectChat}
          onBackToList={() => setActiveChat(null)}
          newMessage={newMessage}
          onNewMessageChange={resizeComposer}
          onSendMessage={handleSendMessage}
          onStartNewChat={handleStartNewChat}
          onNewChat={handleNewChat}
          onNewGroup={() => {
            fetchConnections();
            setShowCreateGroup(true);
          }}
          onRenameChat={handleRenameChat}
          onDeleteChat={handleDeleteChat}
          loading={chatLoading || loading}
          t={t}
          inputRef={inputRef}
        />
      </div>

      {showCreateGroup && (
        <div className="modal-overlay">
          <div className="create-group-modal">
            <h2>{t('network.createGroupTitle')}</h2>
            <input
              type="text"
              placeholder={t('network.groupTitlePlaceholder')}
              value={newGroupTitle}
              onChange={(e) => setNewGroupTitle(e.target.value)}
              className="group-input"
            />
            <div className="input-label">{t('network.endDate')}</div>
            <input
              type="datetime-local"
              value={newGroupEndDate}
              onChange={(e) => setNewGroupEndDate(e.target.value)}
              className="group-input"
            />

            <div className="invite-section">
              <h3>{t('network.inviteMembers')}</h3>

              <div className="invite-search-box">
                <input
                  type="text"
                  placeholder={t('network.invitePlaceholder')}
                  value={inviteSearchQuery}
                  onChange={(e) => setInviteSearchQuery(e.target.value)}
                  className="group-input"
                />
              </div>

              <div className="connections-selector">
                {inviteSearchQuery ? (
                  inviteSearchResults.map(u => {
                    const isMutual = connections.find(c => c.handle === u.handle)?.is_mutual;
                    return (
                      <div
                        key={u.handle}
                        className={`selector-item ${invitedUsers.find(iu => iu.handle === u.handle) ? 'selected' : ''} ${!isMutual ? 'not-mutual' : ''}`}
                        onClick={() => toggleInvitedUser(u)}
                      >
                        <span className="mini-avatar">{u.avatar_url ? <img src={u.avatar_url} alt="av" /> : '👤'}</span>
                        <span>@{u.handle}</span>
                        {!isMutual && <span className="warning-tag">{t('network.notMutual')}</span>}
                      </div>
                    );
                  })
                ) : (
                  connections.filter(c => c.is_mutual).map(conn => (
                    <div
                      key={conn.handle}
                      className={`selector-item ${invitedUsers.find(iu => iu.handle === conn.handle) ? 'selected' : ''}`}
                      onClick={() => toggleInvitedUser(conn)}
                    >
                      <span className="mini-avatar">{conn.avatar_url ? <img src={conn.avatar_url} alt="av" /> : '👤'}</span>
                      <span>@{conn.handle}</span>
                    </div>
                  ))
                )}
                {!inviteSearchQuery && connections.filter(c => c.is_mutual).length === 0 && (
                  <p className="hint">{t('network.noMutualHint')}</p>
                )}
              </div>

              {invitedUsers.length > 0 && (
                <div className="invited-chips">
                  {invitedUsers.map(u => (
                    <span key={u.handle} className="invited-chip" onClick={() => toggleInvitedUser(u)}>
                      @{u.handle} ✕
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowCreateGroup(false)}>{t('common.cancel')}</button>
              <button className="confirm-btn" onClick={handleCreateGroup} disabled={loading}>
                {loading ? t('network.creating') : t('network.createGroupButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QophPage;
